import React, { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';

import useDrive from '../../hooks/useDrive';
import { useNewSaveDocument } from '../../../UploadDocuments/hooks/Useuploaddocuments';
import {
  withRelativePath,
  isHiddenFile,
} from '../../../UploadDocuments/utils/getFilesFromDataTransferItems';

import UploadDropdown from './UploadDropdown';
import UploadProgressModal from '../../modals/UploadProgressModal';

import { Button, Wrapper } from './UploadButton.styles';
// import { MAX_FILE_SIZE_VALUE } from '../../../../settings/constant';
import { useAuth } from '../../../../hooks/useAuth';
import { formatFileSizeFromBytes } from '../../../UploadDocuments/utils';

// Breadcrumbs always start with the root node (e.g. { id: 'root', name: 'Home' }).
// We drop it and join the rest of the trail to get the current folder path,
// e.g. [root, Councilpdf, test] -> "Councilpdf/test".
const buildFolderPathFromBreadcrumbs = (breadcrumbs = []) =>
  breadcrumbs
    .filter((crumb) => crumb.id !== 'root')
    .map((crumb) => crumb.name)
    .join('/');

// Combines the current Drive folder path, the file's relative path (only
// present when uploading a folder with nested subfolders), and the file
// name into a single document path. Any empty segment is dropped so this
// works the same whether we're at root, inside a nested folder, uploading
// a single file, or uploading a folder tree.
const buildDocumentPath = (folderPath, relativePath, fileName) =>
  [folderPath, relativePath, fileName].filter(Boolean).join('/');

// Same helper used in UploadDocuments.jsx — top-level segment of the
// relativePath, used only for folder-progress grouping (FolderProgressBar).
const resolveFolderUploadRoot = (relativePath) => relativePath.split('/').filter(Boolean)[0];

// How long the modal stays open after the last item finishes, so the user
// actually sees the final "done" state instead of an instant close.
const AUTO_CLOSE_DELAY_MS = 700;

const UploadButton = () => {
  const { folderName, applicationId, breadcrumbs } = useDrive();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [uploadItems, setUploadItems] = useState([]);
  const [showProgressModal, setShowProgressModal] = useState(false);

  const wrapperRef = useRef(null);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const autoCloseTimerRef = useRef(null);

  const { mutateAsync: uploadDocument } = useNewSaveDocument();
  const { councilInfo } = useAuth();
  const MAX_FILE_SIZE = councilInfo?.MAX_FILE_SIZE || 50 * 1024 * 1024; // Default to 50MB if not set

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, []);

  // Auto-close the progress modal once every item has reached 'done'.
  // Runs on every uploadItems change, so it fires the same way whether
  // items finished on the first pass or after a manual retry. The cleanup
  // clears any pending timer if items change again before it fires.
  useEffect(() => {
    if (!showProgressModal || uploadItems.length === 0) return undefined;

    const allDone = uploadItems.every(
      (item) => item.status === 'done' || item.status === 'skipped' || item.status === 'size-error'
    );

    if (allDone) {
      autoCloseTimerRef.current = setTimeout(() => {
        setShowProgressModal(false);
      }, AUTO_CLOSE_DELAY_MS);
    }

    return () => {
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
    };
  }, [uploadItems, showProgressModal]);

  const handleUploadFiles = () => {
    fileInputRef.current?.click();
    setOpen(false);
  };

  const handleUploadFolder = () => {
    folderInputRef.current?.click();
    setOpen(false);
  };

  const updateItemStatus = (itemId, patch) => {
    setUploadItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, ...patch } : item))
    );
  };

  // Uploads a single queued item (used both for the initial sequential pass
  // and for a manual retry of one failed item). Returns true/false so the
  // caller can decide whether to surface a failure message.
  const runUploadForItem = async (item) => {
    updateItemStatus(item.id, { status: 'uploading', errorMessage: '', progress: 0 });

    try {
      await uploadDocument({
        file: item.file,
        documentPath: item.documentPath,
        folderName,
        isExisting: true,
        applicationId,
        onUploadProgress: (percent) => {
          updateItemStatus(item.id, { progress: percent });
        },
      });
      updateItemStatus(item.id, { status: 'done', progress: 100, errorMessage: '' });
      return true;
    } catch (err) {
      updateItemStatus(item.id, { status: 'error', progress: 0, errorMessage: 'Upload failed' });
      return false;
    }
  };

  // Uploads each file sequentially (isExisting + applicationId, same as
  // "Upload More Documents" in Application.jsx), then invalidates the
  // application query once at the end so Drive refetches with the new
  // pdfArray. Sequential rather than Promise.all so one failure doesn't
  // cancel the rest and the button state stays predictable.
  //
  // The documentPath is prefixed with the current breadcrumb folder path
  // (e.g. "Councilpdf/test") so files/folders land in the folder the user
  // is currently browsing, not at the Drive root.
  const uploadFiles = async (fileEntries) => {
    const visible = fileEntries.map(withRelativePath).filter((entry) => !isHiddenFile(entry.file));

    if (visible.length === 0) return;

    const folderPath = buildFolderPathFromBreadcrumbs(breadcrumbs);

    const items = visible.map((entry, index) => {
      const isSizeExceeded = entry.file.size > MAX_FILE_SIZE;

      // Only a real folder upload (webkitdirectory input) has a
      // relativePath — this is what tells UploadProgressModal to group
      // this item under a FolderProgressBar instead of rendering it as a
      // standalone row.
      const isFolderUpload = Boolean(entry.relativePath);
      const rootFolder = isFolderUpload ? resolveFolderUploadRoot(entry.relativePath) : undefined;

      return {
        id: `${entry.file.name}-${entry.file.lastModified}-${index}-${Date.now()}`,
        name: entry.file.name,
        file: entry.file,
        documentPath: buildDocumentPath(folderPath, entry.relativePath, entry.file.name),
        isFolderUpload,
        rootFolder,
        status: isSizeExceeded ? 'size-error' : 'pending',
        progress: 0,
        errorMessage: isSizeExceeded
          ? `File exceeds the ${formatFileSizeFromBytes(MAX_FILE_SIZE)} limit.`
          : '',
      };
    });

    setUploadItems(items);
    setShowProgressModal(true);
    setIsUploading(true);

    const failedNames = [];

    for (const item of items) {
      if (item.status === 'size-error') {
        continue;
      }

      const success = await runUploadForItem(item);

      if (!success) {
        failedNames.push(item.name);
      }
    }

    queryClient.invalidateQueries({ queryKey: ['application', applicationId] });
    setIsUploading(false);

    if (failedNames.length > 0) {
      window.alert(
        `Some files failed to upload: ${failedNames.join(', ')}. You can retry them from the upload dialog.`
      );
    }
    // Auto-close is handled by the effect above once all items are 'done'.
  };

  // Retries a single failed item in place. Files that already uploaded
  // successfully are untouched — only the item matching itemId is re-sent.
  const handleRetryItem = async (itemId) => {
    const item = uploadItems.find((i) => i.id === itemId);
    if (!item) return;

    setIsUploading(true);
    await runUploadForItem(item);
    setIsUploading(false);

    queryClient.invalidateQueries({ queryKey: ['application', applicationId] });
  };

  const handleSkipItem = (itemId) => {
    setUploadItems((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? {
              ...item,
              status: 'skipped',
              errorMessage: '',
            }
          : item
      )
    );
  };

  const handleFilesChange = (event) => {
    const files = event.target.files;
    if (files?.length) {
      uploadFiles(Array.from(files));
    }
    event.target.value = '';
  };

  const handleCloseProgressModal = () => {
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
    setShowProgressModal(false);
  };

  return (
    <Wrapper ref={wrapperRef}>
      <Button type="button" onClick={() => setOpen((prev) => !prev)} disabled={isUploading}>
        {isUploading ? <Loader2 size={14} className="spin" /> : null}
        {isUploading ? 'Uploading…' : 'Upload'}
      </Button>

      {open && (
        <UploadDropdown
          onClose={() => setOpen(false)}
          onUploadFiles={handleUploadFiles}
          onUploadFolder={handleUploadFolder}
        />
      )}

      <input ref={fileInputRef} type="file" multiple hidden onChange={handleFilesChange} />

      <input
        ref={folderInputRef}
        type="file"
        multiple
        hidden
        webkitdirectory=""
        directory=""
        onChange={handleFilesChange}
      />

      <UploadProgressModal
        isOpen={showProgressModal}
        items={uploadItems}
        onClose={handleCloseProgressModal}
        onRetry={handleRetryItem}
        onSkip={handleSkipItem}
      />
    </Wrapper>
  );
};

export default UploadButton;
